import { FIRST_LOAD_STATE_WAIT_MS } from 'projects/service/src/app/shared/utils/grid-persistence';
import { TemplateRef } from '@angular/core';
import { ActionIconDef, GridConfig, GridRow } from '@inavista/components/grid';

// Service Order popup and ISC popup → Execution tab → Assignments on @inavista/components. Same columns, labels, default
// visibility and edit rules as the Syncfusion grid it replaces. Rows are the popup's own `assignments` array
// (LocalRowsGridApi, no endpoint); they are saved with the popup's Save, as before.

const BLACK = '#000000de';
const INACTIVE = '#cccccc';
const PLUS_ICON = '<line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />';
const PENCIL_ICON = '<path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />';
const TRASH_ICON = '<polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />';

export const ADD_ASSIGNMENT_ACTION = 'add-assignment';
export const ASSIGNMENTS_PERSISTENCE_KEY = 'rims.service.so-popup-assignments';
export const ISC_ASSIGNMENTS_PERSISTENCE_KEY = 'rims.service.isc-popup-assignments';

// Grid column id → the row field it shows. The description columns show the list's text for the stored code
// (the old foreign-key columns); their editors write the code too.
export const ASSIGNMENT_COLUMNS: [string, string][] = [
  ['activityId', 'int'],
  ['referenceActivityDescription', 'nvarchar'],
  ['serviceTypeDescription', 'nvarchar'],
  ['reportedProblemDescription', 'nvarchar'],
  ['description', 'nvarchar'],
  ['solutionDescription', 'nvarchar'],
  ['priorityText', 'nvarchar'],
  ['startDate', 'datetime'],
  ['endDate', 'datetime'],
];

export interface AssignmentsGridContext {
  /** ASSIGNMENTS_PERSISTENCE_KEY (field service) or ISC_ASSIGNMENTS_PERSISTENCE_KEY: each popup keeps its own layout. */
  persistenceKey: string;
  editors: Record<string, TemplateRef<unknown>>;
  /** Why this row can't be opened for editing, or null when it can (the old beginEdit and rowDataBound rules). */
  editLockReason: (row: GridRow) => string | null;
  /** Why this row's Edit / Delete icons are disabled, or null when they are enabled. */
  actionLockReason: (row: GridRow) => string | null;
  addDisabledReason: () => string | null;
}

export function buildAssignmentsActionIcons(ctx: AssignmentsGridContext): ActionIconDef[] {
  const color = (row: GridRow) => (ctx.actionLockReason(row) ? INACTIVE : BLACK);
  return [
    { key: 'edit', label: 'Edit Assignment', icon: PENCIL_ICON, colorFn: color,
      labelFn: row => ctx.actionLockReason(row) ?? 'Edit Assignment' },
    { key: 'delete', label: 'Delete Assignment', icon: TRASH_ICON, colorFn: color,
      labelFn: row => ctx.actionLockReason(row) ?? 'Delete Assignment' },
  ];
}

export function buildAssignmentsGridConfig(ctx: AssignmentsGridContext): GridConfig {
  return {
    apiBaseUrl: 'page://so-popup-assignments',
    clientSide: true,
    keyColumn: 'rowId',
    // 30, as the Syncfusion grid (and the Transactions grid beside it).
    rowHeight: 30,
    defaultColumnOrder: ASSIGNMENT_COLUMNS.map(([c]) => c),
    // ID, Priority, Start Date and End Date were hidden at start.
    startHiddenColumns: ['activityId', 'priorityText', 'startDate', 'endDate'],
    // The old grid kept these out of the column chooser: they are the assignment.
    alwaysVisibleColumns: ['referenceActivityDescription', 'serviceTypeDescription', 'description'],
    columnLabels: {
      activityId: 'ID', referenceActivityDescription: 'Reference Activity', serviceTypeDescription: 'Service Type',
      reportedProblemDescription: 'Reported Problem', description: 'Description', solutionDescription: 'Solution',
      priorityText: 'Priority', startDate: 'Start Date', endDate: 'End Date',
    },
    columnAlign: Object.fromEntries(ASSIGNMENT_COLUMNS.map(([c]) => [c, {
      header: { horizontal: 'left' as const }, ...(c === 'activityId' ? { horizontal: 'center' as const } : {}),
    }])),
    columnWidths: {
      // Equal widths, as the old grid shared the width between its columns.
      activityId: 190, referenceActivityDescription: 190, serviceTypeDescription: 190, reportedProblemDescription: 190,
      description: 190, solutionDescription: 190, priorityText: 190, startDate: 190, endDate: 190,
    },
    columnTypes: { startDate: 'datetime', endDate: 'datetime' },
    columnDateFormats: { startDate: 'd MMM, yyyy h:mm a', endDate: 'd MMM, yyyy h:mm a' },
    excelFilterColumns: ['referenceActivityDescription', 'serviceTypeDescription', 'reportedProblemDescription', 'solutionDescription', 'priorityText'],
    // No Excel export: the old grid's button never showed (its toolbar was set on the wrong object).
    enableExport: false,
    enableSavedViews: false,
    // The old toolbar: Columns, Add, Search, Clear filters. Reset layout is kept: it undoes the saved layout.
    enableScrollButtons: false,
    enableFitColumns: false,
    enableResetLayout: true,
    enableRefresh: false,
    enableFooter: false,
    editMode: 'inline',
    editSaveMode: 'row',
    // A double click opens the row, as before.
    editTrigger: 'doubleClick',
    editableColumns: ['referenceActivityDescription', 'reportedProblemDescription', 'description', 'solutionDescription', 'priorityText', 'startDate', 'endDate'],
    // Reference Activity is mandatory: the red * in its header (requiredColumns) and the old rule on save (rowValidator).
    // Priority was required too, but a new assignment gets the default priority when it is saved (setAssignmentRow).
    requiredColumns: ['referenceActivityDescription'],
    rowValidator: row => row['referenceActivityDescription'] ? null
      : { column: 'referenceActivityDescription', message: 'Reference Activity: This field is required.' },
    readOnlyCellFn: row => !!ctx.editLockReason(row),
    editorTemplates: ctx.editors,
    deleteMode: 'host',
    rowDeletable: () => false,
    toolbarActions: [{
      key: ADD_ASSIGNMENT_ACTION, label: 'Add Assignment', kind: 'button', icon: PLUS_ICON,
      disabled: () => !!ctx.addDisabledReason(), disabledReason: () => ctx.addDisabledReason() ?? '',
    }],
    emptyStateMessage: 'No assignments yet. Use + to add one.',
    // Right-click a cell filters by its value (Martin, 2026-10-29: every grid that filters).
    enableContextMenuFilter: true,
    persistenceKey: ctx.persistenceKey,
    firstLoadStateWaitMs: FIRST_LOAD_STATE_WAIT_MS,
  };
}
